import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AuthorByline from '../common/AuthorByline';
import SEOHead from '../common/SEOHead';
import { getMissionSEO } from '../../utils/seo';

const useFadeIn = (delay: number = 0) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return {
    ref,
    style: {
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
    } as React.CSSProperties,
  };
};

const Mission: React.FC = () => {
  const s1 = useFadeIn(0);
  const s2 = useFadeIn(0);
  const s3 = useFadeIn(0);
  const s4 = useFadeIn(0);

  return (
    <div className="bg-cream-50">
      <SEOHead seoData={getMissionSEO()} />

      {/* ===== Hero — Massive Typography, White ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[100%] bg-gradient-to-bl from-forest-100/50 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[80%] bg-gradient-to-tr from-forest-50/60 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative section-container py-28 sm:py-36 lg:py-48">
          <div className="max-w-4xl">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-8 block animate-fade-up" style={{ animationDelay: '0.1s', opacity: 0 }}>Our Mission</span>

            <h1 className="font-display text-display-xl sm:text-display-2xl text-forest-900 tracking-tight leading-[0.95] mb-10 animate-fade-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
              Healthcare<br />
              without<br />
              <span className="text-gradient">barriers.</span>
            </h1>

            <p className="text-body-lg sm:text-xl text-warm-500 max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: '0.3s', opacity: 0 }}>
              Essential medications belong to everyone. LiberaCure routes orders directly to licensed Indian generic pharmacies — no warehouse, no markup, no dispensing on our part. Crypto-only checkout, transparent supply chain, structural refusal to ship Schedule II–IV controlled substances. We operate within personal-import frameworks. The barriers we remove are price and friction, not safeguards.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Big Numbers Strip ===== */}
      <section className="border-y border-warm-200">
        <div className="section-container">
          <div ref={s1.ref} style={s1.style} className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-warm-200">
            {[
              { num: '100+', label: 'Medications Available' },
              { num: '50+', label: 'Countries Served' },
              { num: '100%', label: 'Privacy Guaranteed' },
              { num: '24/7', label: 'Customer Support' },
            ].map((stat) => (
              <div key={stat.label} className="py-12 lg:py-16 px-6 lg:px-10 text-center">
                <div className="font-display text-display-md lg:text-display-lg text-forest-900 tracking-tight">{stat.num}</div>
                <p className="text-caption text-warm-500 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Mission Statement — Asymmetric ===== */}
      <section className="py-24 sm:py-32 lg:py-40">
        <div className="section-container">
          <div ref={s2.ref} style={s2.style} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-6 block">Why We Exist</span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
                Everyone deserves affordable, private healthcare.
              </h2>
            </div>

            <div className="lg:col-span-7 space-y-8">
              <p className="text-body-lg text-warm-600 leading-relaxed">
                At LiberaCure, we believe that access to essential medications shouldn't be limited by high costs, long wait times, or unnecessary embarrassment.
              </p>
              <p className="text-body-lg text-warm-600 leading-relaxed">
                We built LiberaCure to change that. By combining an easy online ordering process with discreet delivery and fair pricing, we make it simple for anyone to get the care they need.
              </p>
              <p className="text-body-lg text-warm-600 leading-relaxed">
                We work directly with trusted, licensed manufacturers to offer FDA-approved generics at a fraction of brand-name cost — without compromising quality or safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Core Values — Bento Grid ===== */}
      <section className="bg-cream-100/60 py-24 sm:py-32">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">Core Values</span>
            <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight">What drives us</h2>
          </div>

          <div ref={s3.ref} style={s3.style} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {/* Large card — spans 2 cols */}
            <div className="sm:col-span-2 bg-cream-50 rounded-2xl border border-warm-200 p-10 sm:p-12 hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0 text-gold-400">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-heading-lg text-forest-900 mb-3 tracking-tight">Global Accessibility</h3>
                  <p className="text-body text-warm-500 leading-relaxed max-w-lg">
                    Healthcare should be available to everyone, everywhere. We provide a seamless online experience so you can access essential medications without barriers — no matter where you are.
                  </p>
                </div>
              </div>
            </div>

            {/* Regular card */}
            <div className="bg-cream-50 rounded-2xl border border-warm-200 p-8 sm:p-10 hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring">
              <div className="w-12 h-12 bg-forest-50 rounded-xl flex items-center justify-center mb-6 text-gold-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-display text-heading text-forest-900 mb-3 tracking-tight">Affordability</h3>
              <p className="text-body-sm text-warm-500 leading-relaxed">
                Direct manufacturer partnerships mean generic alternatives at a fraction of brand-name cost.
              </p>
            </div>

            {/* Regular card */}
            <div className="bg-cream-50 rounded-2xl border border-warm-200 p-8 sm:p-10 hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring">
              <div className="w-12 h-12 bg-forest-50 rounded-xl flex items-center justify-center mb-6 text-gold-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="font-display text-heading text-forest-900 mb-3 tracking-tight">Privacy First</h3>
              <p className="text-body-sm text-warm-500 leading-relaxed">
                Your health is personal. Every order is handled with complete discretion and minimal data collection.
              </p>
            </div>

            {/* Wide card — spans 2 cols */}
            <div className="sm:col-span-2 bg-cream-50 rounded-2xl border border-warm-200 p-8 sm:p-10 hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0 text-gold-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-heading text-forest-900 mb-3 tracking-tight">Quality & Safety</h3>
                  <p className="text-body-sm text-warm-500 leading-relaxed max-w-lg">
                    Every product is sourced from licensed, reputable manufacturers. We focus exclusively on FDA-approved generics and clinically established treatments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Privacy — Horizontal Scroll Cards ===== */}
      <section className="py-24 sm:py-32">
        <div className="section-container mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-end">
            <div>
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">Privacy & Trust</span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight">
                Your health,<br />your business.
              </h2>
            </div>
            <p className="text-body text-warm-500 lg:text-right max-w-md lg:ml-auto">
              Every step of the LiberaCure experience is designed with your privacy in mind — from how you order to how your package arrives.
            </p>
          </div>
        </div>

        <div className="section-container">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                num: '01',
                title: 'Fully Remote',
                desc: 'Order and manage everything online. No in-person visits, no phone calls, no waiting rooms.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
                  </svg>
                ),
              },
              {
                num: '02',
                title: 'Discreet Packaging',
                desc: 'Plain, unmarked packaging with no indication of contents. Your privacy is our priority.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                ),
              },
              {
                num: '03',
                title: 'Data Protection',
                desc: 'We collect only what is necessary. Your data is never shared, sold, or used for advertising.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                ),
              },
            ].map((f) => (
              <div key={f.num} className="group bg-forest-900 rounded-2xl p-8 sm:p-10 text-cream-100 relative overflow-hidden hover:scale-[1.02] transition-transform duration-500 ease-spring">
                <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-gold-400/5 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <span className="text-[5rem] sm:text-[6rem] font-display leading-none text-cream-100/5 absolute top-[-20px] right-[-10px] tracking-tighter">{f.num}</span>
                  <div className="w-12 h-12 bg-cream-100/10 rounded-xl flex items-center justify-center mb-8 text-gold-400">
                    {f.icon}
                  </div>
                  <h3 className="font-display text-heading text-cream-100 mb-3 tracking-tight">{f.title}</h3>
                  <p className="text-body-sm text-warm-400 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA — Clean ===== */}
      <section className="border-t border-warm-200">
        <div className="section-container py-24 sm:py-32">
          <div ref={s4.ref} style={s4.style} className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight mb-5">
              Ready to take control?
            </h2>
            <p className="text-body-lg text-warm-500 mb-10 max-w-lg mx-auto">
              Browse our selection of affordable, high-quality medications delivered discreetly worldwide.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
              <Link to="/shop" className="btn-primary gap-2.5">
                Browse Products
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link to="/inquiry" className="btn-secondary">
                Contact Us
              </Link>
            </div>

            <p className="text-micro text-warm-400 leading-relaxed max-w-lg mx-auto pt-8 border-t border-warm-200">
              LiberaCure provides medications for personal use within applicable legal frameworks.
              We strongly recommend consulting with a healthcare professional before starting any new medication.
            </p>

            <div className="max-w-2xl mx-auto text-left">
              <AuthorByline />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mission;
